/**
 * QuickAddRowModal — the "Insert new row" button opens this.
 * Creates a project (if new) + a schedule row for the picked week, in one shot.
 *
 * The form mirrors the visible table columns so Smartsheet users feel at home:
 *   Week# | Task | Expected start | Expected finish | Workers | Days | Status | Team | Comments
 *
 * If the task name matches an existing project (case-insensitive), we append a
 * schedule to that project instead of creating a duplicate.
 */
import React, { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Loader2, X, ListPlus } from "lucide-react";
import { API, STATUS_META, authHeader, inputCls, Field, ModalShell, isoWeekNow, WeekBadge } from "./shared";

export default function QuickAddRowModal({ projects, defaultWeek, onClose, onSaved }) {
  const [form, setForm] = useState({
    week: defaultWeek || isoWeekNow(),
    task_name: "",
    expected_start: "",
    expected_finish: "",
    planned_workers: 4,
    working_days: 5,
    status: "not_started",
    assigned_team: "",
    address: "",
    comments: "",
  });
  const [busy, setBusy] = useState(false);

  const matchedProject = useMemo(() => {
    const q = form.task_name.trim().toLowerCase();
    if (!q) return null;
    return projects.find((p) => (p.name || "").toLowerCase() === q) || null;
  }, [form.task_name, projects]);

  const workerDays = (parseInt(form.planned_workers) || 0) * (parseFloat(form.working_days) || 0);

  const submit = async () => {
    if (!form.task_name.trim()) {
      toast.error("Task name is required");
      return;
    }
    if (!form.week.trim()) {
      toast.error("Week is required");
      return;
    }
    setBusy(true);
    try {
      let projectId = matchedProject?.id;

      if (!projectId) {
        // Create the project first
        const { data: created } = await axios.post(`${API}/projects`, {
          name: form.task_name.trim(),
          client: "",
          address: form.address,
          status: form.status,
          priority: "normal",
          notes: form.comments,
          comments: form.comments,
          assigned_team: form.assigned_team,
          original_expected_start: form.expected_start,
          original_expected_finish: form.expected_finish,
          estimate: {
            teams_required: 1,
            workers_per_team: parseInt(form.planned_workers) || 4,
            productivity_target: 8.0,
            productive_hours_per_day: 7.5,
            manager_planned_duration_days: parseFloat(form.working_days) || 5,
            estimate_status: "draft",
          },
        }, authHeader());
        projectId = created.id;
      }

      // Upsert the schedule row for the week
      await axios.post(`${API}/projects/${projectId}/schedules`, {
        week: form.week.trim(),
        allocated_teams: 1,
        allocated_team_days: workerDays / Math.max(1, parseInt(form.planned_workers) || 4),
        planned_workers: parseInt(form.planned_workers) || 4,
        working_days: parseFloat(form.working_days) || 5,
        expected_start: form.expected_start,
        expected_finish: form.expected_finish,
        assigned_team_ids: [],
        is_tentative: form.status === "tentative",
        notes: form.comments,
      }, authHeader());

      toast.success(matchedProject
        ? `Row added to ${matchedProject.name}`
        : `New project "${form.task_name}" created`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to insert row");
    }
    setBusy(false);
  };

  return (
    <ModalShell onClose={onClose} maxWidth="max-w-3xl" testid="quick-add-modal">
      <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-black flex items-center gap-1">
            <ListPlus className="w-3 h-3"/> Insert new row
          </div>
          <h2 className="text-lg font-black text-slate-100">Quick add — one project, one week</h2>
          {matchedProject && (
            <div className="text-[11px] text-amber-300 mt-1">
              Task name matches <span className="font-bold">{matchedProject.name}</span> — a schedule row will be added to it.
            </div>
          )}
        </div>
        <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-100"><X className="w-5 h-5"/></button>
      </div>

      <div className="p-6 grid grid-cols-2 md:grid-cols-3 gap-4">
        <Field label="Week # *" hint="e.g. 2026-W31">
          <input value={form.week} required
                 onChange={(e) => setForm({ ...form, week: e.target.value })}
                 className={inputCls} placeholder="2026-W31" data-testid="qa-week"/>
        </Field>
        <Field label="Task name *">
          <input value={form.task_name} required data-testid="qa-name"
                 onChange={(e) => setForm({ ...form, task_name: e.target.value })}
                 className={inputCls} placeholder="1457 Maridalsveien 3"/>
        </Field>
        <Field label="Address">
          <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls}/>
        </Field>
        <Field label="Expected start">
          <div className="flex items-center gap-1">
            <input type="date" value={form.expected_start} data-testid="qa-start"
                   onChange={(e) => setForm({ ...form, expected_start: e.target.value })} className={inputCls}/>
            <WeekBadge value={form.expected_start} testId="qa-start-week"/>
          </div>
        </Field>
        <Field label="Expected finish">
          <div className="flex items-center gap-1">
            <input type="date" value={form.expected_finish} data-testid="qa-finish"
                   onChange={(e) => setForm({ ...form, expected_finish: e.target.value })} className={inputCls}/>
            <WeekBadge value={form.expected_finish} testId="qa-finish-week"/>
          </div>
        </Field>
        <Field label="Status">
          <select value={form.status} data-testid="qa-status"
                  onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
            {Object.keys(STATUS_META).map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
          </select>
        </Field>
        <Field label="Workers">
          <input type="number" min="0" value={form.planned_workers} data-testid="qa-workers"
                 onChange={(e) => setForm({ ...form, planned_workers: e.target.value })} className={inputCls}/>
        </Field>
        <Field label="Working days">
          <input type="number" step="0.5" min="0" value={form.working_days} data-testid="qa-days"
                 onChange={(e) => setForm({ ...form, working_days: e.target.value })} className={inputCls}/>
        </Field>
        <Field label="Assigned team">
          <input value={form.assigned_team} onChange={(e) => setForm({ ...form, assigned_team: e.target.value })}
                 className={inputCls} placeholder="Elvis / Bogdan..."/>
        </Field>
        <div className="col-span-2 md:col-span-3">
          <Field label="Comments">
            <textarea rows={2} value={form.comments}
                      onChange={(e) => setForm({ ...form, comments: e.target.value })}
                      className={inputCls} placeholder="Scaffold up today. Info in messenger…"/>
          </Field>
        </div>
        <div className="col-span-2 md:col-span-3 rounded-xl bg-cyan-500/10 border border-cyan-400/30 p-3">
          <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black">Worker-days consumed</div>
          <div className="text-3xl font-black text-slate-100 mt-1">{workerDays.toFixed(1)}</div>
          <div className="text-[10px] text-slate-400">
            {form.planned_workers} workers × {form.working_days} days
          </div>
        </div>
      </div>

      <div className="px-6 py-3 border-t border-white/10 flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-white/10 text-slate-300 text-xs font-black uppercase">
          Cancel
        </button>
        <button onClick={submit} disabled={busy || !form.task_name.trim() || !form.week.trim()}
                data-testid="qa-save"
                className="px-5 py-2 rounded-lg bg-cyan-500 text-slate-900 text-xs font-black uppercase flex items-center gap-1 disabled:opacity-40">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <><Save className="w-3.5 h-3.5"/> Insert row</>}
        </button>
      </div>
    </ModalShell>
  );
}
